// auth.js - Google + email/password sign-in
// All user document writes go through the backend API — never write directly to Firestore.
//
// UID PRESERVATION: every sign-in path LINKS onto the existing anonymous user rather than
// replacing it. A plain signInWithPopup over an anonymous session mints a brand-new uid and
// orphans everything the visitor made while anonymous — fatal here, because the whole flow is
// "generate a video, sign in to watch it". When linking is impossible (the account already
// exists) we fall back to a normal sign-in and hand the old uid to claimAnonVideos() so the
// backend re-points the work at the real account.
import {
  signInWithPopup,
  signInWithRedirect,
  signInWithCredential,
  getRedirectResult,
  getAdditionalUserInfo,
  signOut,
  onAuthStateChanged,
  signInAnonymously,
  linkWithPopup,
  linkWithRedirect,
  linkWithCredential,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  EmailAuthProvider,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { logEvent } from "firebase/analytics";
import { auth, db, canUseFirestore } from "./firebase";
import { getAnalyticsInstance } from "./firebase";
import { trackFunnelEvent } from "./utils/ev";
import { apiService } from "./apiService";
import { holdPosition } from "./utils/arrival";
import { handOverUserJSON } from "./utils/perUserStorage";

import { GoogleAuthProvider } from "firebase/auth";

console.log("🔥 Auth module loading with Google Sign-In...");

// Initialize Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope("email");
googleProvider.addScope("profile");

// Add additional settings for better compatibility
googleProvider.setCustomParameters({
  prompt: "select_account",
});

/* In-flight only. This memo exists to collapse CONCURRENT callers into one anonymous sign-in,
 * NOT to remember forever that we once made one — and it used to do the second thing, which
 * broke signing back in after a sign-out.
 *
 * signOut() clears auth.currentUser but obviously cannot clear a module-level variable. So
 * once anyone had been anonymous, `if (anonInitPromise) return anonInitPromise` returned the
 * stale RESOLVED promise on every later call and minted nothing. After signing out the app
 * therefore had no user at all — not even an anonymous one — and no way to get one short of a
 * full page reload, which is not something a visitor knows to do.
 *
 * Cleared the moment it settles, so it still dedupes the concurrent case (AuthContext races it
 * against an 8s timeout while the sign-in paths await it too) and still re-runs afterwards.
 */
let anonInitPromise = null;

export const ensureAnonUser = async () => {
  // Someone is already signed in — anonymous or not. Nothing to mint.
  if (auth.currentUser) return;
  if (anonInitPromise) return anonInitPromise;

  const pending = (async () => {
    const { user } = await signInAnonymously(auth);

    await new Promise((resolve) => {
      const unsub = onAuthStateChanged(auth, (u) => {
        if (u && u.uid === user.uid) {
          unsub();
          resolve();
        }
      });
    });
  })();

  anonInitPromise = pending;
  // Both arms, and settled here rather than with .finally() so a rejection is observed on this
  // branch instead of surfacing as an unhandled rejection. Callers still get `pending` and are
  // free to handle the failure themselves.
  pending.then(
    () => { anonInitPromise = null; },
    () => { anonInitPromise = null; },
  );
  return pending;
};

// Detect mobile browsers where signInWithPopup is unreliable
const isMobileBrowser = () => {
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod|Android/i.test(ua);
};

// The redirect flow is a DEAD END on localhost, which is why signing in never completes in the
// iOS Simulator. firebase.js only rewrites authDomain to window.location.hostname off-localhost,
// so a local dev server is at localhost:3000 while the OAuth handler is at
// magi-f7f13.firebaseapp.com — cross-site. Safari partitions the storage that carries the
// pending-redirect state, so you come back from Google and getRedirectResult() returns null:
// signed out, no error, nothing to see. Prod is unaffected (there authDomain === hostname), so
// this only changes local behavior — on localhost take the popup path even on a mobile UA.
// See: https://firebase.google.com/docs/auth/web/redirect-best-practices
const isLocalhost = () =>
  ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);

// Errors meaning "this Google/email identity already has its own account", so linking onto the
// anonymous session is impossible and we must sign in normally and migrate the anon work over.
const ALREADY_LINKED_CODES = new Set([
  "auth/credential-already-in-use",
  "auth/email-already-in-use",
  "auth/account-exists-with-different-credential",
]);

const ANON_CLAIM_KEY = "magi_anon_uid_to_claim";

/** Hand the pre-sign-in anonymous uid to the backend so its videos follow the user over.
 *
 *  AND THE CLIENT'S HALF WITH IT. The generation queue and the notification list are stored
 *  per uid (see utils/perUserStorage.js), and this is the one path where a visitor's uid
 *  genuinely changes — every other sign-in LINKS onto the anonymous user and keeps it. Without
 *  the hand-over, a video that was generating at the moment you signed into an existing
 *  account lost its tile: the work migrated server-side and the card tracking it did not.
 */
const claimAnonWork = async (anonUid) => {
  if (!anonUid) return;
  const newUid = auth.currentUser?.uid || null;
  if (newUid && newUid !== anonUid) {
    // Dedupe on the job id, which is what a queue entry is identified by everywhere else.
    handOverUserJSON("magi_generation_queue", anonUid, newUid, { idOf: (j) => j?.uid || j?.id });
    handOverUserJSON("magi_notifications", anonUid, newUid, { idOf: (n) => n?.id });
  }
  try {
    await apiService.claimAnonVideos(anonUid);
  } catch (err) {
    // Never block sign-in on this — the account is valid either way.
    console.error("Anon claim failed:", err);
  }
};

export const signInWithGoogle = async (emailMarketingOptIn = null) => {
  /* HOLD THE SCROLL ACROSS THE TAP.
   *
   * Measured in utils/arrival.js: ~86ms after an interaction iOS expands Safari's toolbars,
   * the visual viewport shrinks, and WebKit scrolls the document down ~34px to compensate.
   * Nothing puts it back. RouteTransitionManager arms holdAtTop for that on every route
   * change — but pressing this button is not a route change. The page stays where it is and
   * then leaves for accounts.google.com, so the compensation landed unanswered and the screen
   * visibly jumped before the navigation. Reported 2026-09-22.
   *
   * HERE RATHER THAN IN THE BUTTON, because there are four entry points into this flow —
   * LandingPage, LoginGate, AuthForm and ReferralPage — and the fault belongs to the flow, not
   * to any one of them.
   *
   * Self-releasing: a touch, wheel or key hands control straight back, and it expires on its
   * own after 1.5s. On the redirect path the page unloads long before that and takes the
   * listeners with it, so `release` matters only when we stay — a popup, or an error. */
  const releaseHold = holdPosition();
  try {
    await ensureAnonUser();

    const anonUser = auth.currentUser;
    const anonUid = anonUser?.isAnonymous ? anonUser.uid : null;

    // Persist opt-in before any redirect (survives page navigation)
    sessionStorage.setItem("magi_email_opt_in", JSON.stringify(emailMarketingOptIn));

    // Mobile Safari blocks popups — use redirect flow instead (but not on localhost, where
    // the redirect silently loses the session; see isLocalhost above)
    if (isMobileBrowser() && !isLocalhost()) {
      sessionStorage.setItem("magi_auth_redirect_pending", "true");
      if (anonUid) sessionStorage.setItem(ANON_CLAIM_KEY, anonUid);
      if (anonUser?.isAnonymous) {
        await linkWithRedirect(anonUser, googleProvider);
      } else {
        await signInWithRedirect(auth, googleProvider);
      }
      // Page will redirect — execution stops here. The hold is left armed deliberately: the
      // chrome settles during the navigation itself, which is the window it exists to cover.
      return { success: true, message: "Redirecting to Google..." };
    }

    let result;
    let claimedUid = null;
    if (anonUser?.isAnonymous) {
      try {
        // Preferred path: keep the uid, so anything made while anonymous stays put.
        result = await linkWithPopup(anonUser, googleProvider);
      } catch (linkError) {
        if (!ALREADY_LINKED_CODES.has(linkError.code)) throw linkError;
        // They already have an account. Sign into it, then migrate the anon work.
        const credential = GoogleAuthProvider.credentialFromError(linkError);
        result = credential
          ? await signInWithCredential(auth, credential)
          : await signInWithPopup(auth, googleProvider);
        claimedUid = anonUid;
      }
    } else {
      result = await signInWithPopup(auth, googleProvider);
    }

    const newUser = result.user;
    const googleLocale = getAdditionalUserInfo(result)?.profile?.locale || null;

    sessionStorage.removeItem("magi_email_opt_in");
    if (claimedUid) await claimAnonWork(claimedUid);
    // Linking succeeded when we started anonymous and did NOT take the claim fallback — the
    // uid carried over, so newUser.uid IS the old anonymous uid. That is the anon→signup
    // conversion, and this is the only place it can be observed.
    const linkedFromAnon = anonUid && !claimedUid ? anonUid : null;
    // Stayed on the page (popup path) — the tap is over, hand scrolling back at once rather
    // than leaving a hold armed for the rest of its 1.5s.
    releaseHold();
    return await processGoogleUser(newUser, false, emailMarketingOptIn, googleLocale, null, linkedFromAnon);
  } catch (error) {
    console.error("Google Sign-In Error:", {
      code: error.code,
      message: error.message,
    });

    // If popup was blocked, fall back to redirect (sessionStorage already saved above).
    // On localhost that fallback is the dead end described at isLocalhost, so say what is
    // actually wrong instead of bouncing through a redirect that cannot come back.
    if (error.code === "auth/popup-blocked" || error.code === "auth/popup-closed-by-browser") {
      if (isLocalhost()) {
        sessionStorage.removeItem("magi_email_opt_in");
        return {
          success: false,
          message:
            "Sign-in popup was blocked. On localhost the redirect fallback cannot complete — " +
            "allow pop-ups for this site (iOS Simulator: Settings > Apps > Safari > Block Pop-ups, off).",
        };
      }
      sessionStorage.setItem("magi_auth_redirect_pending", "true");
      const anonUser = auth.currentUser;
      if (anonUser?.isAnonymous) {
        sessionStorage.setItem(ANON_CLAIM_KEY, anonUser.uid);
        await linkWithRedirect(anonUser, googleProvider);
      } else {
        await signInWithRedirect(auth, googleProvider);
      }
      return { success: true, message: "Redirecting to Google..." };
    }

    sessionStorage.removeItem("magi_email_opt_in");
    releaseHold();
    return { success: false, message: error.message };
  }
};

// ============================================================
// EMAIL + PASSWORD
// ============================================================

/**
 * Where the confirmation link drops you once Firebase has accepted it.
 *
 * Without this, the link lands on Firebase's own "your email is verified" page and the person
 * is stranded there — the tab they STARTED in is the one holding the wall, and nothing tells
 * it anything happened. With it, clicking the link returns them to the app already verified,
 * so the flow completes even if they never look at the original tab again.
 *
 * handleCodeInApp stays false: Firebase applies the code on its own page and then redirects,
 * which means no code-handling route to build or keep correct here.
 */
const verificationSettings = () => ({
  url: `${window.location.origin}/`,
  handleCodeInApp: false,
});

/**
 * Send the confirmation email — ours first, Firebase's as the safety net.
 *
 * OURS IS THE GOOD ONE. Firebase's message links to firebaseapp.com, which Gmail distrusts
 * (a real signup landed in spam), and lands on Google's unbranded handler page. Identity
 * Platform refuses to let either be changed, so the backend mints the same oobCode and sends
 * it over Brevo pointed at /verify instead. See backend/managers/verification_email.py.
 *
 * FALLING BACK IS NOT A BUG, IT IS THE DESIGN. This is the signup path: until the email
 * arrives nobody can use the product, so a worse-looking message that lands always beats a
 * better one that does not. Any refusal — flag off, Brevo down, backend unreachable — drops
 * through to Firebase's own send.
 *
 * On that fallback path a continue URL must be on Firebase's authorized-domains list; an
 * unlisted one throws auth/unauthorized-continue-uri and NOTHING IS SENT, so an unauthorized
 * domain degrades again to a plain send rather than leaving someone waiting forever.
 */
const sendVerification = async (user) => {
  try {
    const res = await apiService.sendVerificationEmail();
    if (res?.sent) return;
    // sent:false is a deliberate refusal the server already logged (disabled, send_failed,
    // or already_verified). Nothing to report here — just take the other path.
  } catch (err) {
    console.error("Branded verification email failed, falling back to Firebase:", err?.message);
  }

  try {
    await sendEmailVerification(user, verificationSettings());
  } catch (err) {
    console.error("Verification email with continue URL failed, retrying plain:", err?.code);
    await sendEmailVerification(user);
  }
};

/** Map Firebase's error codes to copy a human can act on. */
export const authErrorMessage = (code) => {
  switch (code) {
    case "auth/invalid-email":
      return "That email doesn't look right.";
    case "auth/missing-password":
    case "auth/weak-password":
      return "Passwords need to be at least 6 characters.";
    case "auth/email-already-in-use":
    case "auth/credential-already-in-use":
      return "An account with that email already exists. Try signing in instead.";
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "That email and password don't match.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a few minutes and try again.";
    case "auth/network-request-failed":
      return "Connection problem. Check your internet and try again.";
    default:
      return "Something went wrong. Please try again.";
  }
};

/**
 * Create an account with email + password.
 *
 * Links onto the anonymous session so the visitor keeps the uid (and therefore the video they
 * just made). Sends the verification email — the backend withholds free credits until the
 * address is confirmed, since unverified email signup is otherwise the cheapest way to farm us.
 */
export const signUpWithEmail = async (email, password, displayName = "", emailMarketingOptIn = null) => {
  try {
    await ensureAnonUser();
    const anonUser = auth.currentUser;
    const anonUid = anonUser?.isAnonymous ? anonUser.uid : null;

    let result;
    let claimedUid = null;
    if (anonUser?.isAnonymous) {
      const credential = EmailAuthProvider.credential(email, password);
      try {
        result = await linkWithCredential(anonUser, credential);
      } catch (linkError) {
        if (!ALREADY_LINKED_CODES.has(linkError.code)) throw linkError;
        // The address already has an account. Signing in is the right move IF the password
        // they typed happens to be that account's — they are really returning, not new.
        try {
          result = await signInWithEmailAndPassword(auth, email, password);
        } catch (signInError) {
          // It was not their password, so this is a genuine "that email is taken" on a
          // CREATE ACCOUNT form. Reporting the sign-in failure verbatim gave
          // "That email and password don't match." — which reads as nonsense here, because
          // the person is not matching anything, they are registering. Re-raise the
          // ORIGINAL link failure so the message says the email is taken and points at
          // signing in, which is the thing they can actually act on.
          throw linkError;
        }
        claimedUid = anonUid;
      }
    } else {
      result = await createUserWithEmailAndPassword(auth, email, password);
    }

    if (displayName && result.user) {
      try {
        await updateProfile(result.user, { displayName });
      } catch (err) {
        console.error("Display name update failed:", err);
      }
    }

    if (result.user && !result.user.emailVerified) {
      try {
        await sendVerification(result.user);
      } catch (err) {
        console.error("Verification email failed:", err);
      }
    }

    if (claimedUid) await claimAnonWork(claimedUid);
    const synced = await processGoogleUser(result.user, false, emailMarketingOptIn, null, "email");
    return { ...synced, needsVerification: !result.user?.emailVerified };
  } catch (error) {
    console.error("Email sign-up error:", error.code, error.message);
    return { success: false, code: error.code, message: authErrorMessage(error.code) };
  }
};

/** Sign into an existing email + password account. */
export const signInWithEmail = async (email, password) => {
  try {
    const anonUser = auth.currentUser;
    const anonUid = anonUser?.isAnonymous ? anonUser.uid : null;

    const result = await signInWithEmailAndPassword(auth, email, password);
    if (anonUid && result.user?.uid !== anonUid) await claimAnonWork(anonUid);

    const synced = await processGoogleUser(result.user, false, null, null, "email");
    return { ...synced, needsVerification: !result.user?.emailVerified };
  } catch (error) {
    console.error("Email sign-in error:", error.code, error.message);
    return { success: false, code: error.code, message: authErrorMessage(error.code) };
  }
};

export const sendPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error("Password reset error:", error.code);
    return { success: false, message: authErrorMessage(error.code) };
  }
};

export const resendVerification = async () => {
  const user = auth.currentUser;
  if (!user || user.emailVerified) return { success: false };
  try {
    await sendVerification(user);
    return { success: true };
  } catch (error) {
    console.error("Resend verification error:", error.code);
    return { success: false, message: authErrorMessage(error.code) };
  }
};

export const handleRedirectResult = async () => {
  // NO sessionStorage GATE. This used to return early unless `magi_auth_redirect_pending`
  // was present, which made the whole mobile sign-in depend on one sessionStorage key
  // surviving a round trip to accounts.google.com. When it does not — an evicted tab, a
  // restore, an in-app browser handing off to Safari, ITP — getRedirectResult() is never
  // called, the pending credential is never resolved, and you land back on the site quietly
  // signed out with nothing logged but "No redirect pending, skipping".
  //
  // getRedirectResult() is documented as safe to call unconditionally: with no pending
  // redirect it resolves to null. The gate bought one skipped call and cost the flow its
  // only completion step.
  sessionStorage.removeItem("magi_auth_redirect_pending");

  // Recover opt-in preference saved before redirect
  const emailMarketingOptIn = JSON.parse(sessionStorage.getItem("magi_email_opt_in") ?? "null");
  sessionStorage.removeItem("magi_email_opt_in");

  // uid captured before the redirect, so anon work can follow the user over
  const pendingAnonUid = sessionStorage.getItem(ANON_CLAIM_KEY);
  sessionStorage.removeItem(ANON_CLAIM_KEY);

  try {
    const result = await getRedirectResult(auth);
    if (result) {
      const googleLocale = getAdditionalUserInfo(result)?.profile?.locale || null;
      // Linking kept the uid, so there is nothing to migrate in that case.
      if (pendingAnonUid && result.user?.uid !== pendingAnonUid) {
        await claimAnonWork(pendingAnonUid);
      }
      return await processGoogleUser(result.user, false, emailMarketingOptIn, googleLocale);
    }
    return null;
  } catch (error) {
    // linkWithRedirect fails this way when the Google account already exists. Same recovery as
    // the popup path: sign into the real account, then migrate the anonymous work across.
    if (ALREADY_LINKED_CODES.has(error.code)) {
      try {
        const credential = GoogleAuthProvider.credentialFromError(error);
        if (credential) {
          const signedIn = await signInWithCredential(auth, credential);
          if (pendingAnonUid) await claimAnonWork(pendingAnonUid);
          return await processGoogleUser(signedIn.user, false, emailMarketingOptIn, null);
        }
      } catch (recoveryError) {
        console.error("❌ Redirect link recovery failed:", recoveryError);
      }
    }
    console.error("❌ Redirect result error:", error);
    return {
      success: false,
      message: `Redirect sign-in failed: ${error.message}`,
    };
  }
};

// priorAnonUid: the anonymous uid this account was linked FROM, when Firebase kept the uid.
// Only the sign-in call sites know it — by the time the backend sees the request the user is
// no longer anonymous and nothing distinguishes them from a direct signup. Attribution only.
const processGoogleUser = async (user, passwordless_login = false, emailMarketingOptIn = null, googleLocale = null, methodOverride = null, priorAnonUid = null) => {
  const isAnonymousUser = user.isAnonymous;

  // Determine auth method
  let authMethod = "google";
  if (methodOverride) authMethod = methodOverride;
  else if (passwordless_login) authMethod = "link";
  else if (isAnonymousUser) authMethod = "anonymous";

  try {
    // Call the backend sync endpoint — it handles creation, upgrade, and login-touch
    const result = await apiService.syncUser({
      email: user.email || null,
      displayName:
        user.displayName || user.email || (isAnonymousUser ? "Guest" : ""),
      photoURL: user.photoURL || null,
      isAnonymous: isAnonymousUser,
      authMethod,
      emailMarketingOptIn,
      googleLocale,
      priorAnonUid,
    });

    const userData = result.user;
    const isNewUser = result.isNewUser || false;

    // Track signup analytics (frontend-only events)
    if (isNewUser && !isAnonymousUser) {
      trackFunnelEvent("signup");
      const a = getAnalyticsInstance();
      if (a) logEvent(a, "sign_up", { method: "google" });
    }

    if (isNewUser) {
      // Seed default study projects in background (fire and forget)
      apiService.seedStudyContent().catch(() => {});

      return {
        success: true,
        user: userData,
        message:
          "Welcome to Magi! You have 1 free video to start creating.",
        isNewUser: true,
      };
    }

    if (result.action === "upgraded") {
      return {
        success: true,
        user: userData,
        message: "Welcome to Magi!",
        isNewUser: false,
      };
    }

    // Returning user
    return {
      success: true,
      user: userData || {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        credits: { available: 0 },
      },
      message: "Welcome back!",
      isNewUser: false,
    };
  } catch (error) {
    console.error("❌ User sync failed:", error);
    // Fallback: read existing doc if API is down (read-only, no writes)
    if (canUseFirestore()) {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        return {
          success: true,
          user: userDoc.data(),
          message: "Welcome back!",
          isNewUser: false,
        };
      }
    }
    return { success: false, message: error.message };
  }
};

export const checkUserCredits = async (userId, creditsNeeded = 1) => {
  try {
    if (!canUseFirestore()) {
      return {
        hasCredits: false,
        availableCredits: 0,
        creditsNeeded,
      };
    }

    const userDoc = await getDoc(doc(db, "users", userId));
    if (!userDoc.exists()) {
      throw new Error("User not found");
    }

    const userData = userDoc.data();
    const availableCredits = userData.credits?.available || 0;

    return {
      hasCredits: availableCredits >= creditsNeeded,
      availableCredits,
      creditsNeeded,
    };
  } catch (error) {
    console.error("Error checking credits:", error);
    return {
      hasCredits: false,
      availableCredits: 0,
      creditsNeeded,
    };
  }
};

export const getUserData = async (uid, retries = 2) => {
  if (!canUseFirestore()) {
    console.warn("Firestore not available - returning null user data");
    return null;
  }

  for (let i = 0; i <= retries; i++) {
    try {
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        return userDoc.data();
      } else if (i < retries) {
        console.log(`User doc not found, retrying... (${i + 1}/${retries})`);
        await new Promise((resolve) => setTimeout(resolve, 500));
      } else {
        console.warn("User data not found after retries");
        return null;
      }
    } catch (error) {
      console.error("Error getting user data:", error);
      if (i < retries) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      } else {
        return null;
      }
    }
  }
};

export const updateUserDisplayName = async (uid, displayName) => {
  try {
    const result = await apiService.updateUserProfile(uid, { displayName });
    return { success: result.success !== false };
  } catch (error) {
    console.error("Error updating display name:", error);
    return { success: false, message: error.message };
  }
};

export const updateUserAvatar = async (uid, avatarIndex) => {
  try {
    const result = await apiService.updateUserProfile(uid, { avatarIndex });
    return { success: result.success !== false };
  } catch (error) {
    console.error("Error updating avatar:", error);
    return { success: false, message: error.message };
  }
};

// Set only by signOutUser, read by AuthContext. A null auth state means two very different
// things — "they pressed Sign out" and "the session vanished on its own" — and the app must
// not answer both by minting an anonymous user. See the note at the anon-mint site.
export let deliberateSignOut = false;
export const consumeDeliberateSignOut = () => {
  const was = deliberateSignOut;
  deliberateSignOut = false;
  return was;
};

export const signOutUser = async () => {
  try {
    deliberateSignOut = true;
    await signOut(auth);
    // Belt and braces alongside the settle-clear above: whatever is in flight, a sign-out ends
    // the session it was establishing, so the next caller must start a fresh one.
    anonInitPromise = null;
    return {
      success: true,
      message: "Signed out successfully",
    };
  } catch (error) {
    console.error("Error signing out:", error);
    return {
      success: false,
      message: "Error signing out",
    };
  }
};

export const observeAuthState = (callback) => {
  return onAuthStateChanged(auth, callback);
};

console.log("🔥 Auth module loaded with Google Sign-In only");

export { processGoogleUser };
